<!-- src/views/ResetPassword.vue -->
<template>
  <section class="auth section container" style="padding-top:120px">
    <div class="auth-card glass">
      <h1 class="h1 auth-title">重設密碼</h1>

      <!-- 無 token -->
      <div v-if="!code" class="card" style="padding:16px; margin-bottom:14px">
        <p style="margin:0 0 8px">重設連結無效或已過期。</p>
        <router-link to="/forgot" class="auth-link">重新索取重設連結</router-link>
      </div>

      <!-- 顯示表單 -->
      <form v-else class="auth-form" @submit.prevent="submit">
        <label class="label">新密碼</label>
        <div class="pw-wrap">
          <input
            :type="show1 ? 'text' : 'password'"
            v-model="password"
            class="input"
            placeholder="至少 8 位，建議含字母與數字"
            @input="checkStrength"
          />
          <button class="pw-toggle" type="button" @click="show1 = !show1">
            {{ show1 ? '隱藏' : '顯示' }}
          </button>
        </div>
        <p v-if="passwordTooShort" class="auth-error" style="margin-top:4px">
          密碼需至少 8 個字元
        </p>

        <div class="pw-strength" :data-level="strength.level">
          <span class="bar"></span><span class="bar"></span><span class="bar"></span>
          <small class="hint">{{ strength.text }}</small>
        </div>

        <label class="label">確認新密碼</label>
        <div class="pw-wrap">
          <input
            :type="show2 ? 'text' : 'password'"
            v-model="confirm"
            class="input"
            :class="{ 'input--error': showMismatch }"
            placeholder="再次輸入新密碼"
          />
          <button class="pw-toggle" type="button" @click="show2 = !show2">
            {{ show2 ? '隱藏' : '顯示' }}
          </button>
        </div>
        <p v-if="showMismatch" class="auth-error" style="margin-top:4px">兩次密碼不一致</p>

        <button
          class="btn btn--primary auth-btn"
          type="submit"
          :disabled="!canSubmit || loading"
        >
          {{ loading ? '提交中...' : '設定新密碼' }}
        </button>

        <p v-if="errorMsg" class="auth-error">{{ errorMsg }}</p>
        <p v-if="ok" class="auth-success">✅ 密碼已更新，請前往登入。</p>
      </form>

      <p class="muted auth-foot">
        想起密碼了？
        <router-link to="/login" class="auth-link">回登入</router-link>
      </p>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()

// === 讀取 URL token ===
const params = new URLSearchParams(window.location.search)
const raw = params.get('token') || ''
const code = (raw.startsWith('reset:') ? raw.replace(/^reset:/, '') : raw).trim()

const password = ref('')
const confirm = ref('')
const show1 = ref(false)
const show2 = ref(false)
const ok = ref(false)
const loading = ref(false)
const errorMsg = ref('')

// === 密碼強度 ===
const strength = ref({ level: 0, text: '密碼強度：弱' })
function checkStrength() {
  const v = password.value || ''
  let score = 0
  if (v.length >= 8) score++
  if (/[A-Z]/.test(v) && /[a-z]/.test(v)) score++
  if (/\d/.test(v) || /[^A-Za-z0-9]/.test(v)) score++
  const map = ['弱', '中', '強']
  strength.value = {
    level: Math.min(score, 3),
    text: `密碼強度：${map[Math.max(score - 1, 0)]}`,
  }
}

// === 表單條件 ===
const passwordTooShort = computed(() => password.value && password.value.length < 8)
const showMismatch = computed(() => password.value && confirm.value && password.value !== confirm.value)
const canSubmit = computed(() =>
  !!code && !passwordTooShort.value && !showMismatch.value
)

// === 提交 ===
async function submit() {
  errorMsg.value = ''
  ok.value = false
  if (passwordTooShort.value) {
    errorMsg.value = '密碼需至少 8 個字元'
    return
  }
  if (showMismatch.value) {
    errorMsg.value = '兩次密碼不一致'
    return
  }

  loading.value = true
  const payload = { code, new_password: password.value }
  console.log('🔑 ResetPassword payload =>', payload)

  try {
    const res = await axios.post(
      'https://web3schoolapi.web3school.info/api/auth/reset',
      payload,
      { headers: { 'Content-Type': 'application/json' } }
    )
    console.log('📩 API response =>', res.data)

    if (res?.data?.ok) {
      ok.value = true
      setTimeout(() => router.replace('/login'), 1500)
    } else {
      const err = res?.data?.error
      if (err === 'INVALID_OR_EXPIRED_CODE') {
        errorMsg.value = '重設連結無效或已過期，請重新索取連結。'
      } else {
        errorMsg.value = res?.data?.message || '重設失敗，請稍後再試'
      }
    }
  } catch (e) {
    console.error('❌ API error =>', e?.response?.data || e)
    const err = e?.response?.data?.error
    if (err === 'INVALID_OR_EXPIRED_CODE') {
      errorMsg.value = '重設連結無效或已過期，請重新索取連結。'
    } else if (err === 'PASSWORD_TOO_SHORT') {
      errorMsg.value = '密碼需至少 8 個字元'
    } else {
      errorMsg.value = '重設失敗，請稍後再試'
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.auth { display:grid; place-items:center; }
.auth-card {
  width:min(520px, 92vw);
  border:1px solid rgba(255,255,255,.22);
  border-radius:18px;
  padding:26px 22px 20px;
  box-shadow:0 12px 40px rgba(0,0,0,.35);
}
.auth-form { display:grid; gap:10px; }
.auth-btn { width:100%; margin-top:8px; }
.auth-foot { margin-top:14px; }
.auth-link { text-decoration: underline; }
.auth-error { margin-top:10px; color:#ff8a8a; font-weight:600; }
.auth-success { margin-top:10px; color:#7cff9f; font-weight:600; }
.pw-wrap { position: relative; }
.pw-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 6px 10px;
  background: transparent;
  border: none;
  color: #222; /* 深灰字，黑底白底都清楚 */
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.15s ease;
}

.pw-toggle:hover {
  color: #000; /* hover 时更深一点 */
}

.pw-toggle:active {
  transform: translateY(-50%) scale(0.96);
  color: #111;
}

.pw-strength { display:flex; align-items:center; gap:8px; margin-top:4px; }
.pw-strength .bar { width:42px; height:6px; border-radius:6px; background:rgba(255,255,255,.22); }
.pw-strength[data-level="1"] .bar:nth-child(-n+1){ background:#f6c94e; }
.pw-strength[data-level="2"] .bar:nth-child(-n+2){ background:#f1b12a; }
.pw-strength[data-level="3"] .bar:nth-child(-n+3){ background:#22c55e; }
.pw-strength .hint { opacity:.85 }
.input--error {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239,68,68,.25);
}
</style>
